/**
 * The auth revision register.
 *
 * A monotonic counter that advances exactly once per real change to the
 * `(token, authProvider)` pair. It is the "auth changed, the bearer you
 * captured may no longer be the session you are on" trigger for the whole
 * HTTP client chain: a 401-recovery flow captures `{provider, authRevision}`
 * alongside a request's bearer and compares it again before it does anything
 * destructive.
 *
 * ## Why a module-level number and not an atom
 *
 * Every reader is synchronous and none of them re-render: the two
 * module-private `currentAuthRevision()` helpers read fresh, in the same
 * continuation as the return that pairs the value with a token, because a
 * read taken a microtask earlier would pair a pre-await revision with a
 * post-await token. A subscription primitive buys nothing here and a jotai
 * atom would additionally need a store to read through.
 *
 * ## Runtime-only
 *
 * Never persisted and never seeded: a revision restored from storage would
 * describe a session that is no longer running, and a reader comparing
 * against it would call a current context stale (or, worse, a stale one
 * current). It starts at 0 on every boot and only ever goes up.
 *
 * Who advances it is deliberately NOT this module's business — it exposes a
 * bump and a read, and the app wires the one writer.
 */
let authRevision = 0;

/** The current revision. Synchronous, and must stay that way. */
export function readAuthRevision(): number {
  return authRevision;
}

/** Advances the revision by one. The only writer. */
export function bumpAuthRevision(): void {
  authRevision += 1;
}
